import React, { useRef } from "react";
import { Typography, Checkbox, Slider, Input, Button } from "@material-tailwind/react";
import { getMoneyFormat } from "../utils/formatViews";
import { useSelector } from "react-redux";
import { RootState } from "../redux/";
import { CheckCircleSolid } from "iconoir-react";

export interface FilterOptionsProps {
    year_range: number[] | null[];
    cost_range: number[];
    locations: string[];
    statuses: string[];
}

interface DashboardVehicleInventoryFilterDialogProps {
    filterOptions: FilterOptionsProps;
    setFilterOptions: React.Dispatch<React.SetStateAction<FilterOptionsProps>>;
    onApply: () => void;
    onCancel: () => void;
}

const COST_RANGE_MIN = 0;
const COST_RANGE_MAX = 100000;
const COST_RANGE_STEP = 1000;

export const DashboardVehicleInventoryFilterDialog: React.FC<
    DashboardVehicleInventoryFilterDialogProps
> = ({ filterOptions, setFilterOptions, onApply, onCancel }) => {
    const sliderRef = useRef<HTMLDivElement>(null); // Ref for the slider track
    const [options, setOptions] = React.useState<FilterOptionsProps>(filterOptions);
    const locations = ["New York", "Los Angeles", "Chicago"];
    const { constants } = useSelector((state: RootState) => state.auth);

    const handleSliderClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!sliderRef.current) return;

        const slider = sliderRef.current;
        const rect = slider.getBoundingClientRect(); // Get slider dimensions
        const clickX = event.clientX - rect.left; // Distance from the left edge of the slider
        const sliderWidth = rect.width; // Total width of the slider
        const clickedValue = Math.round((clickX / sliderWidth) * COST_RANGE_MAX); // Convert click to value

        // Determine whether to move min or max
        const [min, max] = options.cost_range;
        const distanceToMin = Math.abs(clickedValue - min);
        const distanceToMax = Math.abs(clickedValue - max);

        if (distanceToMin < distanceToMax) {
            setOptions(
                (options: FilterOptionsProps) =>
                    ({
                        ...options,
                        cost_range: [clickedValue, options.cost_range[1]],
                    } as FilterOptionsProps)
            );
        } else {
            setOptions(
                (options: FilterOptionsProps) =>
                    ({
                        ...options,
                        cost_range: [options.cost_range[0], clickedValue],
                    } as FilterOptionsProps)
            );
        }
    };

    return (
        <React.Fragment>
            <div className="p-0 relative overflow-hidden">
                <div className="mb-5 flex flex-col gap-2 relative ">
                    <Button
                        onClick={() => {
                            setOptions((options: FilterOptionsProps) => ({
                                ...options,
                                statuses: [],
                            }));
                        }}
                        variant="ghost"
                        className="!absolute w-max top-0 right-0 text-black p-0 m-0"
                    >
                        Clear All
                    </Button>
                    <Typography variant="small" className="block text-sm text-primary">
                        Status
                    </Typography>
                    {constants?.["VEHICLE_STATUSES"]?.map((status: string[], idx: number) => (
                        <div key={idx} className="flex items-center gap-2">
                            <Checkbox
                                key={Math.random()}
                                id={status[0]}
                                checked={options.statuses.includes(status[0])}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setOptions((options: FilterOptionsProps) => ({
                                        ...options,
                                        statuses: e.target.checked
                                            ? [...options.statuses, status[0]]
                                            : options.statuses.filter((s) => s !== status[0]),
                                    }));
                                }}
                            >
                                <Checkbox.Indicator />
                            </Checkbox>
                            <Typography
                                as="label"
                                htmlFor={status[0]}
                                className="cursor-pointer text-foreground"
                            >
                                {status[1]}
                            </Typography>
                        </div>
                    ))}
                </div>

                <div className="mx-2">
                    <Typography variant="small" className="-ml-2 block text-sm text-primary">
                        Cost
                    </Typography>
                    <div className="relative pt-10 pb-5" onClick={handleSliderClick}>
                        <Slider
                            min={COST_RANGE_MIN}
                            max={COST_RANGE_MAX}
                            step={COST_RANGE_STEP}
                            ref={sliderRef}
                            value={options.cost_range}
                            onValueChange={(newRange) => {
                                setOptions(
                                    (options: FilterOptionsProps) =>
                                        ({
                                            ...options,
                                            cost_range: newRange,
                                        } as FilterOptionsProps)
                                );
                            }}
                        >
                            <Slider.Range />
                            <Slider.Thumb className="!bg-white border-black border-2" />
                        </Slider>

                        <div
                            className="absolute left-2 top-4 translate-x-[-50%]"
                            style={{ left: "0%" }}
                        >
                            <div className="text-gray-400 text-base">
                                {getMoneyFormat(COST_RANGE_MIN)}
                            </div>
                        </div>
                        <div
                            className="absolute left-0 top-4 translate-x-[-50%]"
                            style={{ left: "90%" }}
                        >
                            <div className="text-gray-400 text-base">
                                {getMoneyFormat(COST_RANGE_MAX)}
                            </div>
                        </div>
                        <div
                            className="absolute bg-black rounded-md left-0 top-1 translate-x-[-50%]"
                            style={{
                                left: `${
                                    options.cost_range[0] > 0
                                        ? Math.max(10, (options.cost_range[0] / 100000) * 100)
                                        : 3
                                }%`,
                            }}
                        >
                            <div className="text-white text-base font-semibold px-1.5 py-0.5 rounded shadow-md">
                                {getMoneyFormat(options.cost_range[0])}
                            </div>
                        </div>
                        <div
                            className="absolute bg-black rounded-md left-0 top-1 translate-x-[-50%]"
                            style={{
                                left: `${Math.min(
                                    88,
                                    (options.cost_range[1] / COST_RANGE_MAX) * 100
                                )}%`,
                            }}
                        >
                            <div className="text-white text-base font-semibold px-1.5 py-0.5 rounded shadow-md">
                                {getMoneyFormat(options.cost_range[1])}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-2 mb-5">
                    <Typography variant="small" className="block text-sm text-primary">
                        Year
                    </Typography>
                    <div className="flex gap-2">
                        <Input
                            placeholder="Min Year"
                            type="number"
                            value={options.year_range[0] || ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setOptions(
                                    (options: FilterOptionsProps) =>
                                        ({
                                            ...options,
                                            year_range: [
                                                Number(e.target.value),
                                                options.year_range[1],
                                            ],
                                        } as FilterOptionsProps)
                                )
                            }
                        />
                        <Input
                            placeholder="Max Year"
                            type="number"
                            value={options.year_range[1] || ""}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setOptions(
                                    (options: FilterOptionsProps) =>
                                        ({
                                            ...options,
                                            year_range: [
                                                options.year_range[0],
                                                Number(e.target.value),
                                            ],
                                        } as FilterOptionsProps)
                                )
                            }
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-2 relative ">
                    <Button
                        variant="ghost"
                        className="!absolute w-max top-0 right-0 text-black p-0 m-0"
                        onClick={() => {
                            setOptions((options: FilterOptionsProps) => ({
                                ...options,
                                locations: [],
                            }));
                        }}
                    >
                        Clear All
                    </Button>
                    <Typography variant="small" className="block text-sm text-primary">
                        Location
                    </Typography>
                    {locations.map((location, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                            <Checkbox
                                id={location}
                                checked={options.locations.includes(location)}
                                key={Math.random()}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setOptions((options: FilterOptionsProps) => ({
                                        ...options,
                                        locations: e.target.checked
                                            ? [...options.locations, location]
                                            : options.locations.filter((s) => s !== location),
                                    }));
                                }}
                            >
                                <Checkbox.Indicator />
                            </Checkbox>
                            <Typography
                                as="label"
                                htmlFor={location}
                                className="cursor-pointer text-foreground"
                            >
                                {location}
                            </Typography>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <Button color="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="gradient"
                    color="primary"
                    onClick={() => {
                        setFilterOptions(options);
                        onApply();
                    }}
                >
                    Apply
                    <CheckCircleSolid className="ml-2 w-4 h-4" />
                </Button>
            </div>
        </React.Fragment>
    );
};
